import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BudgetScenarioType } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import {
    ScenarioInputsData,
    ScenarioInputsDraft,
    ScenarioInputsUpdate,
    canonicalDraft,
    draftFromServer,
    draftIssues,
    draftToInputsPayload,
    withPristineRows,
} from '../components/Budget/scenarioDraft';
import {
    EMPTY_SCENARIO_META,
    ScenarioMetaValue,
    hasScenarioDateErrors,
    hasScenarioMetaErrors,
    toScenarioMetaPatch,
} from '../components/Budget/scenarioMeta';
import { scenarioMonths } from '../components/Budget/helpers';
import { BudgetScenario } from '../services/budgetScenario';
import { isConflictError, useUpdateBudgetScenario } from './api/budgetScenarios';

interface UseScenarioFormArgs {
    scenarioId: number | undefined;
    scenario: BudgetScenario | undefined;
    /** The scenario's (immutable) type — decides which draft shape and which editor is in play. */
    type: BudgetScenarioType | undefined;
    /** The active type's GET response; undefined while it loads or for a type without an editor. */
    inputsData: ScenarioInputsData | undefined;
}

/** Draft + the clean state it is measured against, in ONE state atom.
 *
 *  They must move together: a save resets the baseline without touching the draft, and if the baseline
 *  lived in a ref that reset would not re-render — `isDirty` would keep reporting the pre-save answer. */
interface FormState {
    meta: ScenarioMetaValue;
    inputs: ScenarioInputsDraft | null;
    baseMeta: ScenarioMetaValue;
    baseInputs: ScenarioInputsDraft | null;
    /** The `updatedAt` the baseline was read from — exactly what the next save must send as its
     *  `revision`. It belongs to the baseline rather than to the query, because the query lags: its
     *  refetch lands after the save resolves, so a second save in that window would otherwise submit
     *  the pre-save revision and be rejected as a conflict it did not actually lose. */
    baseRevision: string | undefined;
}

const isFormDirty = (form: FormState): boolean =>
    JSON.stringify(form.meta) !== JSON.stringify(form.baseMeta) ||
    canonicalDraft(form.inputs) !== canonicalDraft(form.baseInputs);

/** `updatedAt` travels as an ISO string over the wire; the type says Date, so normalise either. */
const toRevision = (updatedAt: Date | string | undefined): string | undefined => {
    if (updatedAt == null) return undefined;
    return updatedAt instanceof Date ? updatedAt.toISOString() : String(updatedAt);
};

// The scenario page as a single form (CALL-783): metadata plus the active type's whole input set, saved
// in one PATCH and reverted as a whole by Cancel. Before this the page autosaved every cell and offered
// a Save that only covered the metadata, so there was no state a user could abandon — and no way to
// review a set of changes before committing them.
export const useScenarioForm = ({ scenarioId, scenario, type, inputsData }: UseScenarioFormArgs) => {
    const { t } = useTranslation('budget');
    const { mutateAsync: updateScenario, isLoading: isSaving } = useUpdateBudgetScenario();

    const serverMeta = useMemo<ScenarioMetaValue | null>(
        () =>
            scenario
                ? {
                      name: scenario.name,
                      description: scenario.description ?? '',
                      type: scenario.type,
                      budgetVersionId: scenario.budgetVersionId,
                      startDate: scenario.startDate ?? '',
                      endDate: scenario.endDate ?? '',
                  }
                : null,
        [scenario]
    );
    const serverInputs = useMemo(() => draftFromServer(type, inputsData), [type, inputsData]);
    // The revision comes from the INPUTS response, not from the scenario query, because it has to describe
    // the state the INPUT ROWS were read in — those are what the next PATCH replaces wholesale.
    //
    // The two are separate queries that refetch independently (window focus, remount, the post-save
    // invalidateScenarioDependents). Pairing `serverInputs` with the scenario query's `updatedAt` meant a
    // colleague committing between the two server reads left the form holding OLD rows under a NEW
    // revision: still "clean", so it adopted them, and its next save passed the concurrency guard and had
    // replaceInputs destroy the colleague's rows. Taken from the inputs response the pair can only be
    // stale TOGETHER, which the server answers with a 409.
    //
    // The scenario query is the fallback for two safe cases: the inputs have not loaded yet, and a type
    // with no editor at all. Both leave `form.inputs` null, `save` then sends no `inputs` block, and a
    // metadata-only PATCH touches no input row — so there is nothing for a mispaired revision to lose.
    const serverRevision = useMemo(
        () => toRevision(inputsData?.revision) ?? toRevision(scenario?.updatedAt),
        [inputsData?.revision, scenario?.updatedAt]
    );

    const [form, setForm] = useState<FormState | null>(null);
    // A 409: the scenario changed under us. The draft is kept — the user chooses whether to keep it or
    // to throw it away for the server's version.
    const [hasConflict, setHasConflict] = useState(false);

    // Adopt server state ONLY while the form is clean. The query object changes identity on every
    // background refetch — a window refocus, or a consolidated-view invalidation from another tab — and
    // overwriting the draft there would wipe a long editing session without a trace.
    useEffect(() => {
        if (!serverMeta) return;
        const fromServer = (): FormState => ({
            meta: serverMeta,
            inputs: serverInputs,
            baseMeta: serverMeta,
            baseInputs: serverInputs,
            baseRevision: serverRevision,
        });
        setForm((prev) => {
            if (!prev || !isFormDirty(prev)) {
                // A row the user added and has not filled in is not a change (canonicalDraft ignores it), so
                // the form counts as clean and lands here — and the server never sent that row, so a plain
                // adopt deleted it under the cursor. It is carried across instead, into the baseline too:
                // equal to the draft, it keeps the form clean (CALL-784).
                const carried = withPristineRows(serverInputs, prev?.inputs ?? null);
                return { ...fromServer(), inputs: carried, baseInputs: carried };
            }
            // The inputs query resolves after the metadata one, so a name typed in between would
            // otherwise leave the editors showing an empty draft for the rest of the session. An
            // unloaded draft cannot hold an edit, so adopting its first arrival loses nothing.
            if (prev.inputs === null && serverInputs !== null) {
                return { ...prev, inputs: serverInputs, baseInputs: serverInputs };
            }
            return prev;
        });
    }, [serverMeta, serverInputs, serverRevision]);

    const meta = form?.meta ?? EMPTY_SCENARIO_META;
    const inputs = form?.inputs ?? null;

    const setMeta = useCallback((next: ScenarioMetaValue) => {
        setForm((prev) => (prev ? { ...prev, meta: next } : prev));
    }, []);

    // Functional END TO END: an updater is applied to the draft the form HOLDS, not to the one its caller last
    // rendered with. That is what makes a gesture changing several cells at once survive — ag-grid flushes the
    // whole burst of cellValueChanged events in one macrotask, so N grid handlers run with no render between
    // them and every one of them has to see the previous one's result (see DraftUpdater in scenarioDraft).
    // A plain draft is still accepted for the paths where there is nothing to thread — the scope multiselect
    // commits one selection per gesture.
    const setInputs = useCallback((next: ScenarioInputsUpdate) => {
        setForm((prev) => (prev ? { ...prev, inputs: typeof next === 'function' ? next(prev.inputs) : next } : prev));
    }, []);

    const isDirty = form != null && isFormDirty(form);
    const issues = useMemo(() => draftIssues(inputs, t), [inputs, t]);
    const metaHasErrors = useMemo(() => hasScenarioMetaErrors(meta, t), [meta, t]);
    const canSave = isDirty && !metaHasErrors && issues.length === 0 && !isSaving;

    // Columns follow the DRAFT period, so extending Start/Ende shows the new months to fill in before the
    // change is even saved — but only while that period is USABLE. Editing a date passes through states that
    // are empty ("DD.MM.YYYY"), half-typed ("15.09.0026") or inverted, and rebuilding the block from those
    // emptied the whole right half of the grid on a keystroke: the user was handed a table with no months and
    // nothing saying why (CALL-784). The columns hold their last valid set instead, and the offending field
    // carries the message. Nothing is risked by holding them — the payload sends every month a row HOLDS,
    // not the visible ones — and it is also what keeps a typo'd end YEAR from rendering 2400 columns before
    // the period-too-long message can be read.
    const periodHasErrors = useMemo(
        () => hasScenarioDateErrors(meta.startDate, meta.endDate, t),
        [meta.startDate, meta.endDate, t]
    );
    const lastValidMonths = useRef<string[]>([]);
    const months = useMemo(() => {
        if (periodHasErrors) return lastValidMonths.current;
        lastValidMonths.current = scenarioMonths(meta.startDate, meta.endDate);
        return lastValidMonths.current;
    }, [meta.startDate, meta.endDate, periodHasErrors]);

    const cancel = useCallback(() => {
        setForm((prev) =>
            prev
                ? // Pristine rows survive Cancel for the same reason they survive a refetch: they are not part
                  // of what Cancel discards.
                  { ...prev, meta: prev.baseMeta, inputs: withPristineRows(prev.baseInputs, prev.inputs) }
                : prev
        );
        setHasConflict(false);
    }, []);

    const save = useCallback(async () => {
        if (!scenarioId || !form || metaHasErrors || issues.length > 0) return;
        const submitted = { meta: form.meta, inputs: form.inputs };
        const revision = form.baseRevision;
        // Ship the `inputs` block ONLY when the draft really differs from its baseline. It used to go out
        // whenever a draft was loaded at all, so renaming a scenario transmitted the whole cached input set
        // — and `replaceInputs` is authoritative, so that rename could delete rows a colleague had added
        // since the cache was filled. A metadata-only save is now a metadata-only PATCH that cannot reach
        // an input table. `canonicalDraft` is the same payload-level comparison `isDirty` uses, so "the
        // inputs changed" here means exactly what it means to the user.
        const inputsChanged = canonicalDraft(submitted.inputs) !== canonicalDraft(form.baseInputs);
        try {
            const saved = await updateScenario({
                id: scenarioId,
                // The same rule as the `inputs` gate below, applied field by field: a PATCH names only the
                // metadata the user actually changed, measured against the baseline the draft was loaded
                // with. Resending an untouched field re-asserted its (possibly stale) value and could revert
                // a colleague's concurrent rename without a conflict — see toScenarioMetaPatch.
                ...toScenarioMetaPatch(submitted.meta, form.baseMeta),
                // Sent either way: harmless on a metadata-only patch (the server only guards on it when
                // `inputs` is present) and required the moment inputs are.
                ...(revision ? { revision } : {}),
                ...(inputsChanged && submitted.inputs ? { inputs: draftToInputsPayload(submitted.inputs) } : {}),
            });
            // Adopt the submitted draft as the clean baseline NOW, without waiting for the refetch. The
            // cache is still the pre-save response for a moment, so a baseline taken from it would leave
            // the form "dirty" — and the adopt-only-while-clean rule above would then lock the draft out
            // of server state permanently.
            // The response carries the row's new updatedAt, so the next save is already based on it and
            // need not wait for the invalidation refetch to catch up.
            setForm((prev) =>
                prev
                    ? {
                          ...prev,
                          baseMeta: submitted.meta,
                          baseInputs: submitted.inputs,
                          baseRevision: toRevision(saved?.updatedAt) ?? prev.baseRevision,
                      }
                    : prev
            );
            setHasConflict(false);
        } catch (error) {
            // The mutation's onError raised the toast and its onSettled already invalidated the scenario
            // and its inputs, so the fresh server state is on its way in either case; the draft stays for
            // a retry.
            if (isConflictError(error as Error)) setHasConflict(true);
        }
    }, [scenarioId, form, metaHasErrors, issues.length, updateScenario]);

    /** Throw the draft away for the (already refetched) server state. */
    const reloadFromServer = useCallback(() => {
        if (!serverMeta) return;
        setForm({
            meta: serverMeta,
            inputs: serverInputs,
            baseMeta: serverMeta,
            baseInputs: serverInputs,
            baseRevision: serverRevision,
        });
        setHasConflict(false);
    }, [serverMeta, serverInputs, serverRevision]);

    const keepEditing = useCallback(() => setHasConflict(false), []);

    return {
        meta,
        setMeta,
        inputs,
        setInputs,
        months,
        isDirty,
        issues,
        canSave,
        isSaving,
        conflict: { isConflicted: hasConflict, reloadFromServer, keepEditing },
        cancel,
        save,
    };
};
